/**
 * Loan Stage Capability Tags
 *
 * Cross-cutting contract between the per-business `LoanStage` rows
 * (29-case-management-service) and downstream systems that need to
 * react to "what kind of stage is this" without hardcoding stage
 * names. Reports, AI agents, document pipeline, pricing engine,
 * notifications, and the consumer portal mapping all key off these
 * tags — never off `LoanStage.name`.
 *
 * The vocabulary lives here in shared-types so all services can
 * import the same constants. Storage is `String[]` on the LoanStage
 * row, which makes the set additive: a new tag is a deploy-only
 * change with no migration on existing rows.
 *
 * Adding a tag: append below + add `LOAN_STAGE_CAPABILITY_META`
 * entry. Removing a tag is a breaking change — coordinate consumers
 * before deletion.
 */

/* eslint-disable @typescript-eslint/no-redeclare */

import { ConsumerLoanStage } from './loan-stage.enums';

// ============================================================================
// CAPABILITY VOCABULARY
// ============================================================================

export const LoanStageCapability = {
  // Lifecycle anchors — used by default seeding + consumer mapping
  IS_INITIAL: 'is_initial', // Cases land here on creation
  IS_TERMINAL: 'is_terminal', // Cases never leave
  IS_FUNDED: 'is_funded', // Money disbursed; pricing/billing trigger
  IS_SERVICING: 'is_servicing', // Post-fund servicing window

  // Workflow triggers — consumed by AI agents, document pipeline, automations
  TRIGGERS_INTAKE: 'triggers_intake',
  TRIGGERS_REVIEW: 'triggers_review',
  TRIGGERS_TERM_SHEET: 'triggers_term_sheet',
  TRIGGERS_TERM_SHEET_SIGNED: 'triggers_term_sheet_signed',
  TRIGGERS_CREDIT_PULL: 'triggers_credit_pull',
  TRIGGERS_APPRAISAL: 'triggers_appraisal',
  TRIGGERS_TITLE_ORDER: 'triggers_title_order',
  TRIGGERS_UNDERWRITING: 'triggers_underwriting',
  TRIGGERS_CLOSING: 'triggers_closing',
  TRIGGERS_DOCS_SIGNED: 'triggers_docs_signed',

  // Approval routing
  REQUIRES_COMMITTEE: 'requires_committee',
  REQUIRES_QC: 'requires_qc',

  // Outcome classification (terminal subtypes)
  TERMINAL_FUNDED: 'terminal_funded',
  TERMINAL_DECLINED: 'terminal_declined',
  TERMINAL_WITHDRAWN: 'terminal_withdrawn',
  TERMINAL_EXPIRED: 'terminal_expired',
  TERMINAL_CANCELLED: 'terminal_cancelled',
  TERMINAL_CHARGED_OFF: 'terminal_charged_off',
} as const;

export type LoanStageCapability = (typeof LoanStageCapability)[keyof typeof LoanStageCapability];

/** Every capability value, useful for runtime validation. */
export const ALL_LOAN_STAGE_CAPABILITIES: readonly LoanStageCapability[] = Object.values(LoanStageCapability);

const CAPABILITY_SET: ReadonlySet<string> = new Set<string>(ALL_LOAN_STAGE_CAPABILITIES);

export function isLoanStageCapability(value: string): value is LoanStageCapability {
  return CAPABILITY_SET.has(value);
}

// ============================================================================
// META FOR CONTROL CENTER UI
// ============================================================================

export type LoanStageCapabilityGroup = 'lifecycle' | 'trigger' | 'approval' | 'terminal';

export interface LoanStageCapabilityMetaEntry {
  /** Friendly toggle label shown next to the checkbox. */
  label: string;
  /** Long-form tooltip copy explaining the effect of enabling this tag. */
  description: string;
  /** UI grouping for the capability picker. */
  group: LoanStageCapabilityGroup;
}

export const LOAN_STAGE_CAPABILITY_META: Record<LoanStageCapability, LoanStageCapabilityMetaEntry> = {
  [LoanStageCapability.IS_INITIAL]: {
    label: 'Cases land here on creation',
    description: 'New loans start in this stage when no other entry stage is specified.',
    group: 'lifecycle',
  },
  [LoanStageCapability.IS_TERMINAL]: {
    label: 'Loans never leave this stage (terminal)',
    description:
      'Mark a stage as terminal so the lifecycle gate stops routing further transitions. Reports and dashboards key off this to compute pipeline conversion.',
    group: 'lifecycle',
  },
  [LoanStageCapability.IS_FUNDED]: {
    label: 'Money is disbursed in this stage',
    description: 'Triggers pricing-engine activation and billing event emission when a case enters this stage.',
    group: 'lifecycle',
  },
  [LoanStageCapability.IS_SERVICING]: {
    label: 'Loan is in active servicing',
    description: 'Routes the loan to the servicing dashboards and enables servicing-only automations.',
    group: 'lifecycle',
  },
  [LoanStageCapability.TRIGGERS_INTAKE]: {
    label: 'Triggers intake collection',
    description: 'AI intake agents and consumer-portal application flow target stages with this tag.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_REVIEW]: {
    label: 'Triggers initial review',
    description: 'Generic "in review" signal — borrowers see this as the REVIEWING step.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_TERM_SHEET]: {
    label: 'Triggers term sheet generation',
    description: 'Automations that draft term sheets fire when a case enters this stage.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_TERM_SHEET_SIGNED]: {
    label: 'Term sheet has been signed',
    description: 'Borrowers see this as the OFFER ACCEPTED step in the consumer portal.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_CREDIT_PULL]: {
    label: 'Triggers credit pull',
    description: 'Credit bureau integrations fire on entry to this stage.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_APPRAISAL]: {
    label: 'Triggers appraisal extraction',
    description: 'Appraisal AI agents and document pipeline rules activate for this stage.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_TITLE_ORDER]: {
    label: 'Triggers title order',
    description: 'Title vendor integrations are notified on entry to this stage.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_UNDERWRITING]: {
    label: 'Triggers underwriting workflow',
    description:
      'Underwriting queues, credit memo generation, and exception handling are scoped to stages with this tag.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_CLOSING]: {
    label: 'Triggers closing coordination',
    description: 'Closing checklists and scheduling automations fire when a case enters this stage.',
    group: 'trigger',
  },
  [LoanStageCapability.TRIGGERS_DOCS_SIGNED]: {
    label: 'Closing documents have been signed',
    description: 'Used by the consumer portal to advance the borrower stepper to the CLOSING step.',
    group: 'trigger',
  },
  [LoanStageCapability.REQUIRES_COMMITTEE]: {
    label: 'Requires committee review',
    description: 'Surfaces a committee review checklist; reports filter committee-required loans by this tag.',
    group: 'approval',
  },
  [LoanStageCapability.REQUIRES_QC]: {
    label: 'Requires post-close QC',
    description: 'Routes the loan to the QC queue and blocks downstream transitions until QC completes.',
    group: 'approval',
  },
  [LoanStageCapability.TERMINAL_FUNDED]: {
    label: 'Terminal: funded outcome',
    description: 'Categorizes terminal stage as a successful close. Pull-through reports use this tag.',
    group: 'terminal',
  },
  [LoanStageCapability.TERMINAL_DECLINED]: {
    label: 'Terminal: declined',
    description: 'Marks the stage as an adverse-action outcome. ECOA notice generation hooks fire on entry.',
    group: 'terminal',
  },
  [LoanStageCapability.TERMINAL_WITHDRAWN]: {
    label: 'Terminal: withdrawn',
    description: 'Borrower-initiated termination. Reports use this tag to separate withdrawals from declines.',
    group: 'terminal',
  },
  [LoanStageCapability.TERMINAL_EXPIRED]: {
    label: 'Terminal: expired',
    description: 'Time-based termination (term sheet expired, rate lock expired, commitment expired).',
    group: 'terminal',
  },
  [LoanStageCapability.TERMINAL_CANCELLED]: {
    label: 'Terminal: cancelled',
    description: 'Admin-initiated cancellation (duplicate, data quality issue, etc.).',
    group: 'terminal',
  },
  [LoanStageCapability.TERMINAL_CHARGED_OFF]: {
    label: 'Terminal: charged off',
    description: 'Servicing-side terminal outcome for non-performing loans.',
    group: 'terminal',
  },
};

// ============================================================================
// CONSUMER PORTAL MAPPING
// ============================================================================

/**
 * Maps each capability tag to its corresponding ConsumerLoanStage. Used by
 * `toConsumerLoanStage()` (in loan-stage.enums.ts) to derive the borrower-
 * facing stage from the lender's per-business stage row, regardless of how
 * the lender renamed their internal stages.
 *
 * Terminal capabilities deliberately omitted: terminal stages render as an
 * alert card in the consumer portal, not as a stepper position.
 */
export const CAPABILITY_TO_CONSUMER_STAGE: Partial<Record<LoanStageCapability, ConsumerLoanStage>> = {
  [LoanStageCapability.TRIGGERS_INTAKE]: ConsumerLoanStage.APPLIED,
  [LoanStageCapability.TRIGGERS_REVIEW]: ConsumerLoanStage.REVIEWING,
  [LoanStageCapability.TRIGGERS_UNDERWRITING]: ConsumerLoanStage.REVIEWING,
  [LoanStageCapability.TRIGGERS_CREDIT_PULL]: ConsumerLoanStage.REVIEWING,
  [LoanStageCapability.TRIGGERS_APPRAISAL]: ConsumerLoanStage.REVIEWING,
  [LoanStageCapability.TRIGGERS_TITLE_ORDER]: ConsumerLoanStage.REVIEWING,
  [LoanStageCapability.TRIGGERS_TERM_SHEET]: ConsumerLoanStage.OFFER,
  [LoanStageCapability.TRIGGERS_TERM_SHEET_SIGNED]: ConsumerLoanStage.ACCEPTED,
  [LoanStageCapability.TRIGGERS_CLOSING]: ConsumerLoanStage.CLOSING,
  [LoanStageCapability.TRIGGERS_DOCS_SIGNED]: ConsumerLoanStage.CLOSING,
  [LoanStageCapability.IS_FUNDED]: ConsumerLoanStage.FUNDED,
  [LoanStageCapability.IS_SERVICING]: ConsumerLoanStage.ACTIVE,
};

/**
 * Priority order when a stage carries multiple capabilities and they map
 * to different consumer stages. Later (more advanced) lifecycle positions
 * win — a stage tagged BOTH triggers_underwriting AND triggers_closing
 * resolves to CLOSING, not REVIEWING.
 */
const CONSUMER_STAGE_PRIORITY: Record<ConsumerLoanStage, number> = {
  [ConsumerLoanStage.APPLIED]: 0,
  [ConsumerLoanStage.REVIEWING]: 1,
  [ConsumerLoanStage.OFFER]: 2,
  [ConsumerLoanStage.ACCEPTED]: 3,
  [ConsumerLoanStage.CLOSING]: 4,
  [ConsumerLoanStage.FUNDED]: 5,
  [ConsumerLoanStage.ACTIVE]: 6,
  [ConsumerLoanStage.TERMINAL]: -1, // handled separately
};

const TERMINAL_CAPABILITIES: ReadonlySet<string> = new Set<string>([
  LoanStageCapability.IS_TERMINAL,
  LoanStageCapability.TERMINAL_FUNDED,
  LoanStageCapability.TERMINAL_DECLINED,
  LoanStageCapability.TERMINAL_WITHDRAWN,
  LoanStageCapability.TERMINAL_EXPIRED,
  LoanStageCapability.TERMINAL_CANCELLED,
  LoanStageCapability.TERMINAL_CHARGED_OFF,
]);

export function hasTerminalCapability(capabilities: readonly string[] | null | undefined): boolean {
  if (!capabilities || capabilities.length === 0) return false;
  for (const cap of capabilities) {
    if (TERMINAL_CAPABILITIES.has(cap)) return true;
  }
  return false;
}

/**
 * Resolves the borrower-facing stage from a set of capability tags. Used
 * by the rewritten `toConsumerLoanStage()` helper. Returns
 * `ConsumerLoanStage.TERMINAL` for any terminal-tagged stage. When no
 * capability matches, falls back to `ConsumerLoanStage.REVIEWING` (most
 * common middle-stage) and surfaces a console.warn so admins can spot
 * mis-tagged stages.
 */
export function capabilitiesToConsumerStage(capabilities: readonly string[] | null | undefined): ConsumerLoanStage {
  if (hasTerminalCapability(capabilities)) {
    return ConsumerLoanStage.TERMINAL;
  }
  if (!capabilities || capabilities.length === 0) {
    return ConsumerLoanStage.REVIEWING;
  }

  let best: ConsumerLoanStage | null = null;
  let bestPriority = -Infinity;
  for (const cap of capabilities) {
    const target = CAPABILITY_TO_CONSUMER_STAGE[cap as LoanStageCapability];
    if (!target) continue;
    const priority = CONSUMER_STAGE_PRIORITY[target];
    if (priority > bestPriority) {
      best = target;
      bestPriority = priority;
    }
  }
  return best ?? ConsumerLoanStage.REVIEWING;
}
